import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Card, Col, Empty, Form, message, Modal, Row, Select, Spin, Tooltip, Typography } from 'antd';
import { PlusOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { getDefaultRoute, getUserRoleCode, isMasterUser } from '../helpers/accessControl';
import { useAuth } from '../context/AuthContext';
import '../style/CompanySelection.css';

const { Title, Text } = Typography;

interface CompanyMembership {
    membershipId: number;
    joinedAt: string;
    company: {
        id: number;
        name: string;
        logoUrl?: string | null;
    };
    role: {
        id: number;
        code: string;
        description: string;
    };
    joinMethod: string;
    license?: {
        planCode?: string | null;
        status?: string | null;
        startsAt?: string | null;
        endsAt?: string | null;
        billingCycle?: 'monthly' | 'annual' | null;
    } | null;
    trialRemainingDays?: number | null;
}

interface UpgradePlanOption {
    code: string;
    name: string;
    description?: string | null;
}

const PLAN_NAME_BY_CODE: Record<string, string> = {
    trial: 'Trial',
    solo: 'OSCAR Solo',
    lite: 'OSCAR Lite',
    basic: 'OSCAR Basic',
    pro: 'OSCAR Pro',
    enterprise: 'Enterprise',
};

const formatPlanName = (planCode?: string | null) => {
    const normalized = String(planCode || '').trim().toLowerCase();
    if (!normalized) return 'No plan';
    return PLAN_NAME_BY_CODE[normalized] || normalized.toUpperCase();
};

const formatLicenseStatus = (status?: string | null) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return null;
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatBillingCycle = (billingCycle?: 'monthly' | 'annual' | null) => {
    if (!billingCycle) return null;
    return billingCycle === 'annual' ? 'Annual billing' : 'Monthly billing';
};

const shouldShowStatusBadge = (planCode?: string | null, status?: string | null) => {
    const normalizedPlan = String(planCode || '').trim().toLowerCase();
    const normalizedStatus = String(status || '').trim().toLowerCase();
    if (!normalizedStatus) return false;
    if (!normalizedPlan) return true;
    return normalizedPlan !== normalizedStatus;
};

const CompanySelection: React.FC = () => {
    const { currentUser, loginUser, logoutUser: logout } = useAuth();
    const [upgradeForm] = Form.useForm();
    const [memberships, setMemberships] = useState<CompanyMembership[]>(() => {
        console.log('[CompanySelection] Hydrating memberships from currentUser context', currentUser?.companyMemberships);
        return currentUser?.companyMemberships || [];
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [switchingId, setSwitchingId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const [upgradeTarget, setUpgradeTarget] = useState<CompanyMembership | null>(null);
    const [upgradePlans, setUpgradePlans] = useState<UpgradePlanOption[]>([]);
    const [upgradePlansLoading, setUpgradePlansLoading] = useState(false);
    const [canCreateCompany, setCanCreateCompany] = useState<boolean>(
        currentUser?.hasCreatedCompany === true ? false : true
    );
    const isMaster = isMasterUser(currentUser);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchMemberships = async () => {
            console.log('[CompanySelection] Fetching memberships from backend...');
            setLoading(true);
            try {
                const response = await axios.get(ENDPOINTS.GET_MEMBERSHIPS, {
                    withCredentials: true,
                });
                const fetched = response.data?.memberships || [];
                if (fetched.length > 0) {
                    setMemberships(fetched);
                    console.log('[CompanySelection] Membership fetch success', fetched);
                } else {
                    console.warn('[CompanySelection] Membership fetch returned empty, keeping cached memberships');
                }

                if (typeof response.data?.canCreateCompany === 'boolean') {
                    setCanCreateCompany(response.data.canCreateCompany);
                } else if (typeof response.data?.hasCreatedCompany === 'boolean') {
                    setCanCreateCompany(!response.data.hasCreatedCompany);
                }
            } catch (error) {
                console.error('Unable to load memberships', error);
                message.error('Unable to load your companies. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchMemberships();
    }, []);

    const handleSelectCompany = async (companyId: number) => {
        if (!currentUser) {
            message.error('You must be logged in to continue.');
            navigate('/login');
            return;
        }

        console.log('[CompanySelection] Selecting company', companyId);
        setSwitchingId(companyId);
        try {
            const response = await axios.post(
                ENDPOINTS.SWITCH_COMPANY,
                { companyId },
                { withCredentials: true },
            );

            const token = response.data?.accessToken;
            if (token) {
                localStorage.setItem('accessToken', token);
                localStorage.setItem('oscar_access_token', token);
                console.log('[CompanySelection] Stored new access token for company', companyId);
            }

            const selectedMembership = memberships.find((m) => m.company.id === companyId);
            const nextUser = {
                ...currentUser,
                currentCompanyId: companyId,
                currentCompanyRole: selectedMembership?.role || response.data?.roleInCurrentCompany,
                companyMemberships: memberships,
            };

            loginUser(nextUser);

            message.success(`You're now working on ${response.data?.currentCompany?.name || 'the selected company'}`);
            const nextRoute = getDefaultRoute(isMasterUser(nextUser), getUserRoleCode(nextUser));
            console.log('[CompanySelection] Company selection complete, navigating to', nextRoute);
            navigate(nextRoute);
        } catch (error) {
            console.error('Unable to switch company', error);
            message.error('Failed to select company. Please try again.');
        } finally {
            setSwitchingId(null);
        }
    };

    const handleDeleteAccount = () => {
        if (!currentUser) {
            message.error('You must be logged in to continue.');
            navigate('/login');
            return;
        }

        Modal.confirm({
            title: 'Delete account',
            content: 'This will deactivate your account and remove all company memberships. You will need to contact support to restore access.',
            okText: 'Delete account',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setDeleting(true);
                try {
                    await axios.delete(ENDPOINTS.DELETE_SELF, { withCredentials: true });
                    message.success('Account deleted successfully.');
                    await logout();
                    navigate('/login');
                } catch (error: any) {
                    const errorMessage = error.response?.data?.message || 'Failed to delete account';
                    message.error(errorMessage);
                } finally {
                    setDeleting(false);
                }
            }
        });
    };

    const handleCreateCompany = async () => {
        if (!currentUser) {
            message.error('You must be logged in to continue.');
            navigate('/login');
            return;
        }
        navigate('/create-company');
    };

    const handleOpenBackOffice = () => {
        navigate('/back-office');
    };

    const fetchUpgradePlans = async () => {
        setUpgradePlansLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.ACTIVE_SUBSCRIPTION_PLANS, { withCredentials: true });
            const plans = Array.isArray(response.data?.data) ? response.data.data : [];
            const filtered = plans.filter((plan: UpgradePlanOption) => {
                const code = String(plan.code || '').toLowerCase();
                return code !== 'trial' && code !== 'enterprise';
            });
            setUpgradePlans(filtered);
            return filtered as UpgradePlanOption[];
        } catch (error) {
            console.error('Failed to load upgrade plans', error);
            message.error('Failed to load available plans.');
            return [];
        } finally {
            setUpgradePlansLoading(false);
        }
    };

    const openUpgradeModal = async (membership: CompanyMembership) => {
        setUpgradeTarget(membership);
        const plans = await fetchUpgradePlans();
        if (plans.length === 0) {
            message.warning('No upgrade plans are currently available.');
            return;
        }
        upgradeForm.setFieldsValue({
            planCode: plans[0].code,
            billingCycle: 'monthly',
        });
        setUpgradeModalOpen(true);
    };

    const handleUpgradeFromCompanySelection = async () => {
        if (!upgradeTarget?.company?.id) return;
        try {
            const values = await upgradeForm.validateFields();
            const companyId = upgradeTarget.company.id;
            const planCode = String(values.planCode || '').trim().toLowerCase();
            const billingCycle = values.billingCycle === 'annual' ? 'annual' : 'monthly';
            const successUrl = encodeURIComponent('/login?subscription=success');
            const cancelUrl = encodeURIComponent('/login?subscription=cancel');
            setUpgrading(true);
            navigate(`/subscription-checkout?flow=upgrade&companyId=${companyId}&plan=${encodeURIComponent(planCode)}&cycle=${billingCycle}&successUrl=${successUrl}&cancelUrl=${cancelUrl}`);
        } catch (error) {
            // Validation errors are handled by antd form.
        } finally {
            setUpgrading(false);
            setUpgradeModalOpen(false);
        }
    };

    const getUserDisplayName = () => {
        const user = currentUser as any;
        const fullName = user?.fullName || user?.name;
        if (fullName) return fullName;
        const firstName = user?.firstName;
        const lastName = user?.lastName;
        if (firstName || lastName) {
            return [firstName, lastName].filter(Boolean).join(' ');
        }
        return null;
    };

    const getUserInitials = (value?: string | null) => {
        if (!value) return 'U';
        return value
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase();
    };

    const getCompanyInitials = (name: string) => {
        return name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase();
    };

    const displayName = getUserDisplayName();
    const userAny = currentUser as any;
    const avatarSrc =
        userAny?.avatarUrl ||
        userAny?.photoUrl ||
        userAny?.profileImageUrl ||
        userAny?.imageUrl;

    const trialMemberships = memberships
        .filter((membership) => membership?.license?.status === 'trial' && typeof membership.trialRemainingDays === 'number')
        .sort((a, b) => Number(a.trialRemainingDays ?? Number.MAX_SAFE_INTEGER) - Number(b.trialRemainingDays ?? Number.MAX_SAFE_INTEGER));
    const nearestTrial = trialMemberships[0] || null;
    const nearestTrialDays = nearestTrial?.trialRemainingDays ?? null;

    return (
        <div className="company-selection-page">
            <div className="company-selection-shell">
                {nearestTrial && (
                    <div className="company-selection-trial-banner">
                        <div className="company-selection-trial-banner-title">Trial Active</div>
                        <div className="company-selection-trial-banner-text">
                            {nearestTrialDays === 0
                                ? `${nearestTrial.company.name} trial has ended. Upgrade now to avoid service interruption.`
                                : `${nearestTrial.company.name} trial ends in ${nearestTrialDays} day${nearestTrialDays === 1 ? '' : 's'}.`}
                        </div>
                    </div>
                )}
                <div className="company-selection-top">
                    <div className="company-selection-header">
                        <Text className="company-selection-kicker">Workspace</Text>
                        <Title level={2}>Select a company</Title>
                        <Text className="company-selection-subtitle">
                            Pick the company context you want to work in for this session.
                        </Text>
                    </div>
                    <Card className="company-selection-account">
                        <div className="company-selection-user">
                            <Avatar
                                size={56}
                                src={avatarSrc}
                                icon={!avatarSrc ? <UserOutlined /> : undefined}
                                className="company-selection-avatar"
                            >
                                {!avatarSrc && getUserInitials(displayName || currentUser?.email)}
                            </Avatar>
                            <div className="company-selection-user-info">
                                <Text type="secondary">Signed in as</Text>
                                <Title level={5} style={{ marginTop: 4 }}>
                                    {displayName || currentUser?.email}
                                </Title>
                                {displayName && currentUser?.email && (
                                    <Text type="secondary" className="company-selection-email">
                                        {currentUser.email}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <Row gutter={8} className="company-selection-actions">
                            <Col span={12}>
                                <Button block onClick={() => logout()}>
                                    Logout
                                </Button>
                            </Col>
                            <Col span={12}>
                                <Button danger block loading={deleting} onClick={handleDeleteAccount}>
                                    Delete account
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                </div>

                {loading ? (
                    <div className="company-selection-loading">
                        <Spin tip="Loading your companies..." />
                    </div>
                ) : memberships.length === 0 ? (
                    <div className="company-selection-list">
                        <Row gutter={[16, 16]}>
                            {isMaster && (
                                <Col xs={24} md={12} lg={8}>
                                    <Card className="company-card company-card-create" hoverable>
                                        <div className="company-card-head">
                                            <div className="company-card-logo company-card-logo-create">
                                                <SettingOutlined />
                                            </div>
                                            <div className="company-card-title">
                                                <Title level={4}>Back Office</Title>
                                                <Text type="secondary">Manage subscriptions and platform settings</Text>
                                            </div>
                                        </div>
                                        <Tooltip title="Open Back Office">
                                            <Button
                                                type="primary"
                                                block
                                                className="company-card-button"
                                                icon={<SettingOutlined />}
                                                onClick={handleOpenBackOffice}
                                            >
                                                Open back office
                                            </Button>
                                        </Tooltip>
                                    </Card>
                                </Col>
                            )}
                            <Col xs={24} md={12} lg={8}>
                                <Card className="company-card company-card-create" hoverable>
                                    <div className="company-card-head">
                                        <div className="company-card-logo company-card-logo-create">
                                            <PlusOutlined />
                                        </div>
                                        <div className="company-card-title">
                                            <Title level={4}>Create Company</Title>
                                            <Text type="secondary">
                                                {canCreateCompany
                                                    ? 'Set up your company workspace'
                                                    : 'You already used your one-time creation'}
                                            </Text>
                                        </div>
                                    </div>
                                    <Tooltip title={canCreateCompany ? 'Start company setup' : 'One-time company creation already used'}>
                                        <Button
                                            type="primary"
                                            block
                                            className="company-card-button"
                                            icon={<PlusOutlined />}
                                            onClick={handleCreateCompany}
                                            disabled={!canCreateCompany}
                                        >
                                            Create company
                                        </Button>
                                    </Tooltip>
                                </Card>
                            </Col>
                        </Row>
                        {!canCreateCompany && (
                            <div className="company-selection-empty" style={{ marginTop: 16 }}>
                                <Empty description="No companies available. Ask an administrator to add you to a company." />
                            </div>
                        )}
                        {canCreateCompany && (
                            <div className="company-selection-empty" style={{ marginTop: 16 }}>
                                <Text type="secondary">
                                    You can create a company now or wait for a company administrator to invite you.
                                    Device access remains unavailable until your account belongs to a company.
                                </Text>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="company-selection-list">
                        <Row gutter={[16, 16]}>
                            {memberships.map((membership, index) => (
                                <Col xs={24} md={12} lg={8} key={membership.membershipId}>
                                    <Card
                                        className="company-card"
                                        hoverable
                                        style={{ ['--card-delay' as any]: `${index * 0.05}s` }}
                                    >
                                        <div className="company-card-head">
                                            <div className="company-card-logo">
                                                {membership.company.logoUrl ? (
                                                    <img src={membership.company.logoUrl} alt={`${membership.company.name} logo`} />
                                                ) : (
                                                    <span>{getCompanyInitials(membership.company.name)}</span>
                                                )}
                                            </div>
                                            <div className="company-card-title">
                                                <Title level={4}>{membership.company.name}</Title>
                                                <Text type="secondary">{membership.role.description}</Text>
                                                {membership.license?.planCode ? (
                                                    <div className="company-card-plan-row">
                                                        <Text className="company-card-plan-badge">
                                                            {formatPlanName(membership.license.planCode)}
                                                        </Text>
                                                        {shouldShowStatusBadge(membership.license.planCode, membership.license.status) &&
                                                            formatLicenseStatus(membership.license.status) && (
                                                            <Text
                                                                className={`company-card-status-pill company-card-status-${String(membership.license.status || '').toLowerCase()}`}
                                                            >
                                                                {formatLicenseStatus(membership.license.status)}
                                                            </Text>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Text className="company-card-plan-meta company-card-plan-meta-missing">
                                                        Plan not assigned
                                                    </Text>
                                                )}
                                                {formatBillingCycle(membership.license?.billingCycle) && (
                                                    <Text className="company-card-plan-meta">
                                                        {formatBillingCycle(membership.license?.billingCycle)}
                                                    </Text>
                                                )}
                                                {membership.license?.status === 'trial' && typeof membership.trialRemainingDays === 'number' && (
                                                    <Text className="company-card-trial-text">
                                                        Trial: {membership.trialRemainingDays} day{membership.trialRemainingDays === 1 ? '' : 's'} remaining
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            type="primary"
                                            block
                                            className="company-card-button"
                                            onClick={() => handleSelectCompany(membership.company.id)}
                                            loading={switchingId === membership.company.id}
                                        >
                                            Use this company
                                        </Button>
                                        {membership.joinMethod === 'company_creation' && (
                                            <Button
                                                block
                                                className="company-card-upgrade-button"
                                                onClick={() => openUpgradeModal(membership)}
                                            >
                                                Upgrade plan
                                            </Button>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                            {isMaster && (
                                <Col xs={24} md={12} lg={8} key="back-office-card">
                                    <Card className="company-card company-card-create" hoverable>
                                        <div className="company-card-head">
                                            <div className="company-card-logo company-card-logo-create">
                                                <SettingOutlined />
                                            </div>
                                            <div className="company-card-title">
                                                <Title level={4}>Back Office</Title>
                                                <Text type="secondary">Manage subscriptions and platform settings</Text>
                                            </div>
                                        </div>
                                        <Tooltip title="Open Back Office">
                                            <Button
                                                type="primary"
                                                block
                                                className="company-card-button"
                                                icon={<SettingOutlined />}
                                                onClick={handleOpenBackOffice}
                                            >
                                                Open back office
                                            </Button>
                                        </Tooltip>
                                    </Card>
                                </Col>
                            )}
                            <Col xs={24} md={12} lg={8} key="create-company-card">
                                <Card className="company-card company-card-create" hoverable>
                                    <div className="company-card-head">
                                        <div className="company-card-logo company-card-logo-create">
                                            <PlusOutlined />
                                        </div>
                                        <div className="company-card-title">
                                            <Title level={4}>Create Company</Title>
                                            <Text type="secondary">
                                                {canCreateCompany
                                                    ? 'Set up another workspace'
                                                    : 'One-time company creation already used'}
                                            </Text>
                                        </div>
                                    </div>
                                    <Tooltip title={canCreateCompany ? 'Start company setup' : 'One-time company creation already used'}>
                                        <Button
                                            type="primary"
                                            block
                                            className="company-card-button"
                                            icon={<PlusOutlined />}
                                            onClick={handleCreateCompany}
                                            disabled={!canCreateCompany}
                                        >
                                            Create company
                                        </Button>
                                    </Tooltip>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                <Modal
                    title={upgradeTarget ? `Upgrade ${upgradeTarget.company.name}` : 'Upgrade plan'}
                    open={upgradeModalOpen}
                    onCancel={() => setUpgradeModalOpen(false)}
                    onOk={handleUpgradeFromCompanySelection}
                    okText="Continue to payment"
                    confirmLoading={upgrading}
                    destroyOnClose
                >
                    <Form
                        form={upgradeForm}
                        layout="vertical"
                        initialValues={{
                            planCode: 'solo',
                            billingCycle: 'monthly',
                        }}
                    >
                        <Form.Item
                            name="planCode"
                            label="Plan"
                            rules={[{ required: true, message: 'Select a plan' }]}
                        >
                            <Select
                                loading={upgradePlansLoading}
                                placeholder="Select plan"
                                options={upgradePlans.map((plan) => ({
                                    value: plan.code,
                                    label: `${plan.name} (${plan.code})`,
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="billingCycle"
                            label="Billing cycle"
                            rules={[{ required: true, message: 'Select billing cycle' }]}
                        >
                            <Select
                                options={[
                                    { value: 'monthly', label: 'Monthly' },
                                    { value: 'annual', label: 'Annual' },
                                ]}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};

export default CompanySelection;
